import { DateTime } from 'luxon';
import {
  AnyAction,
  combineReducers
} from 'redux';

import {
  isClearRecentlyModified,
  isCreateLibrarySuccess,
  isGetLibrariesSuccess,
  isGetLibraryCountSuccess,
  isGetLibrarySuccess,
  isPublishLibrarySuccess,
  isUpdateLibrarySuccess
} from './actions';
import {
  isEntityType,
  isLibrarySnapshotStatus,
  Libraries,
  LibraryDetailed,
  LibraryDetailedResponse,
  LibraryListing,
  LibraryListingResponse,
  LibrarySnapshotStatus
} from './state';

export const INITIAL_STATE: Libraries = {
  allCodes: [],
  byCode: {},
  count: null,
  recentlyModified: []
};

// Utility functions for parsing raw server responses

function parseLibraryListingSnapshot(
  snapshot: LibraryListingResponse['librarySnapshot']
): LibraryListing['librarySnapshot'] {
  if (!isLibrarySnapshotStatus(snapshot.status)) {
    throw new Error(`Invalid library snapshot status: "${snapshot.status}"`);
  }
  return {
    code: snapshot.code,
    created: DateTime.fromISO(snapshot.created),
    status: snapshot.status
  };
}
export function parseLibraryListing(
  listing: LibraryListingResponse
): LibraryListing {
  return {
    ...listing,
    created: DateTime.fromISO(listing.created),
    lastPublished: listing.lastPublished == null ? null : DateTime.fromISO(listing.lastPublished),
    librarySnapshot: parseLibraryListingSnapshot(listing.librarySnapshot),
    updated: DateTime.fromISO(listing.updated)
  };
}

function parseLibraryDetailedSnapshot(
  snapshot: LibraryDetailedResponse['librarySnapshot']
): LibraryDetailed['librarySnapshot'] {
  return {
    code: snapshot.code,
    created: DateTime.fromISO(snapshot.created),
    entitySnapshots: snapshot.entitySnapshots.map(function parseEntitySnapshot(entitySnapshot) {
      if (!isEntityType(entitySnapshot.type)) {
        throw new Error(`Invalid entity snapshot type: "${entitySnapshot.type}"`);
      }
      return {
        code: entitySnapshot.code,
        id: entitySnapshot.id,
        type: entitySnapshot.type,
        version: entitySnapshot.version
      };
    })
  };
}
export function parseLibraryDetailed(
  library: LibraryDetailedResponse
): LibraryDetailed {
  return {
    ...library,
    created: DateTime.fromISO(library.created),
    lastPublished: library.lastPublished == null ? null : DateTime.fromISO(library.lastPublished),
    librarySnapshot: parseLibraryDetailedSnapshot(library.librarySnapshot),
    updated: DateTime.fromISO(library.updated)
  };
}

// Reducers

function byCode(state = INITIAL_STATE.byCode, action: AnyAction): Libraries['byCode'] {
  // add all new codes
  if (isGetLibrariesSuccess(action)) {
    const updatedLibraries = action.payload.data;
    // replace the entire previous state so as to not keep any stale data
    const updatedState = {} as Libraries['byCode'];
    for (const library of updatedLibraries) {
      const code = library.code;
      updatedState[code] = parseLibraryListing(library);
    }
    return updatedState;
  }

  // add the newly created code
  if (isCreateLibrarySuccess(action) || isGetLibrarySuccess(action) || isUpdateLibrarySuccess(action)) {
    const library = parseLibraryDetailed(action.payload.data);
    const code = library.code;

    // if the library was already fetched, merge with what we already had rather than replacing

    const previous = state[code];
    // don't use a utility like ramda's mergeDeep, as that will merge the luxon DateTime objects too
    const next = {
      ...previous,
      ...library,
      // only merge the library snapshot if it's the same snapshot
      librarySnapshot: previous?.librarySnapshot.code === library.librarySnapshot.code
        ? { ...previous?.librarySnapshot, ...library.librarySnapshot }
        : library.librarySnapshot // otherwise just replace it with the new snapshot
    };
    return { ...state, [code]: next };
  }

  // update the lastPublished field for a newly published library
  if (isPublishLibrarySuccess(action)) {
    const code = action.meta.previousAction.code;
    const library = state[code];
    if (library != null) {
      const snapshotCode = action.payload.data.librarySnapshot;
      if (library.librarySnapshot.code !== snapshotCode) {
        // realistically this error _could_ foreseeably happen, if a new version of the
        // library was saved by someone else while this user was publishing it, and so
        // they didn't have the latest snapshot loaded.
        // the best fix would probably be for the endpoint response to include the full
        // published snapshot, rather than just its code.
        throw new Error('Library snapshot does not match published snapshot');
      }

      const publishTimestamp = action.payload.data.published;
      const updatedLibrary = {
        ...library,
        lastPublished: DateTime.fromISO(publishTimestamp),
        librarySnapshot: {
          ...library.librarySnapshot,
          // having the response endpoint respond with the full snapshot rather than
          // just the code would also avoid having to "guess" its status here
          status: 'PUBLISHED' as LibrarySnapshotStatus
        }
      };

      return { ...state, [code]: updatedLibrary };
    }
  }

  return state;
}
function allCodes(state = INITIAL_STATE.allCodes, action: AnyAction): Libraries['allCodes'] {
  // add all new codes
  if (isGetLibrariesSuccess(action)) {
    const updatedLibraries = action.payload.data;
    return updatedLibraries.map((library) => library.code);
  }

  // add the newly created code
  if (isCreateLibrarySuccess(action) || isGetLibrarySuccess(action) || isUpdateLibrarySuccess(action)) {
    const library = action.payload.data;
    const code = library.code;
    const isAlreadyExisting = state.includes(code);
    return isAlreadyExisting ? state : [...state, code];
  }
  return state;
}

function count(state = INITIAL_STATE.count, action: AnyAction): Libraries['count'] {
  if (isGetLibraryCountSuccess(action)) {
    return action.payload.data.count;
  } else if (isCreateLibrarySuccess(action)) {
    return state !== null ? (state + 1) : state;
  }
  return state;
}

function recentlyModified(
  state = INITIAL_STATE.recentlyModified,
  action: AnyAction
): Libraries['recentlyModified'] {
  // clear all
  if (isClearRecentlyModified(action)) {
    return [];
  }

  // add to recently modified
  if (isCreateLibrarySuccess(action) || isUpdateLibrarySuccess(action)) {
    const library = action.payload.data;
    const code = library.code;
    const isAlreadyExisting = state.includes(code);
    return isAlreadyExisting ? state : [ code, ...state ];
  }

  return state;
}

export const libraries = combineReducers({
  allCodes,
  byCode,
  count,
  recentlyModified
});
